import { BIBLE_BOOK_ORDER, getBibleBookFullName, parseReadingPlanReference } from './scriptureReferences';
import { DEFAULT_SEARCH_FIELDS } from '../config/config-helper';

const rawFieldValue = (result, field) => {
  const value = result?.[field]?.raw ?? result?.[field]?.snippet ?? '';
  return Array.isArray(value) ? value.join(' ') : String(value || '');
};

const plainText = value => String(value || '')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/gi, ' ');

const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const scripturePatterns = reference => {
  if (!reference) return [];

  const books = [...new Set([
    reference.book,
    reference.bookname,
    getBibleBookFullName(reference.book),
  ].filter(Boolean))];
  const chapters = [];
  for (let chapter = reference.chapter; chapter <= reference.chapterEnd; chapter += 1) {
    chapters.push(chapter);
  }

  return books.flatMap(book => chapters.map(chapter => {
    const bookPattern = escapeRegExp(book);
    const chapterPattern = `${chapter}(?!\\d)`;
    if (reference.verseStart !== null && chapter === reference.chapter) {
      return new RegExp(`${bookPattern}\\s*(?:第\\s*)?${chapterPattern}\\s*(?:章\\s*)?[:：]\\s*${reference.verseStart}(?!\\d)`, 'i');
    }
    return new RegExp(`${bookPattern}\\s*(?:第\\s*)?${chapterPattern}(?:\\s*章)?`, 'i');
  }));
};

const matchesAny = (value, patterns) => {
  const text = plainText(value);
  return patterns.some(pattern => pattern.test(text));
};

const scriptureRelevanceScore = (result, reference) => {
  const patterns = scripturePatterns(reference);
  if (!patterns.length) return 0;

  const resultBook = plainText(rawFieldValue(result, 'chapter_code_name') || rawFieldValue(result, 'chapter_name')).replace(/\s+/g, '');
  const resultChapter = Number(rawFieldValue(result, 'chapter_number'));
  const requestedBooks = [reference.book, reference.bookname, getBibleBookFullName(reference.book)]
    .filter(Boolean)
    .map(value => value.replace(/\s+/g, ''));
  const structuredChapterMatch = requestedBooks.includes(resultBook)
    && resultChapter >= reference.chapter
    && resultChapter <= reference.chapterEnd;
  const resultVerse = Number(rawFieldValue(result, 'verse_number'));
  const structuredVerseMatch = structuredChapterMatch
    && reference.verseStart !== null
    && resultVerse >= reference.verseStart
    && resultVerse <= reference.verseEnd;

  let score = structuredChapterMatch ? 200 : 0;
  if (structuredVerseMatch) score += 300;
  if (matchesAny(rawFieldValue(result, 'verselist'), patterns)) score += 140;
  if (matchesAny(rawFieldValue(result, 'title'), patterns)) score += 110;
  if (matchesAny(rawFieldValue(result, 'tag'), patterns)) score += 80;
  if (matchesAny(rawFieldValue(result, 'description'), patterns)) score += 50;
  return score;
};

const isScriptureResult = result => plainText(rawFieldValue(result, 'doctype')).trim() === '經文';

const commonChineseCharacters = new Set('的了是我你他她們也這那在有和與為一個就而又不人上下之其所能可'.split(''));

const contentPairs = value => {
  const text = plainText(value).replace(/[^\u3400-\u9fff]/g, '');
  const pairs = new Set();
  for (let index = 0; index < text.length - 1; index += 1) {
    const first = text[index];
    const second = text[index + 1];
    if (!commonChineseCharacters.has(first) && !commonChineseCharacters.has(second)) {
      pairs.add(`${first}${second}`);
    }
  }
  return pairs;
};

export const getScriptureContentQuery = result =>
  plainText(rawFieldValue(result, 'title')).replace(/\s+/g, ' ').trim().slice(0, 100);

export const rankScriptureContentResults = (results, verseText, reference, limit = 4) => {
  const versePairs = contentPairs(verseText);
  if (versePairs.size < 2 || !Array.isArray(results)) return [];
  const bookNumber = BIBLE_BOOK_ORDER.indexOf(reference?.book) + 1;

  return results
    .map((result, index) => {
      const content = ['title', 'description', 'prayer', 'tag', 'verse']
        .map(field => rawFieldValue(result, field)).join(' ').slice(0, 60000);
      const matchingPairs = contentPairs(content);
      const matchCount = [...versePairs].filter(pair => matchingPairs.has(pair)).length;
      const sameChapter = bookNumber > 0
        && Number(rawFieldValue(result, 'book')) === bookNumber
        && Number(rawFieldValue(result, 'chapter')) === reference.chapter;
      const citesVerse = Boolean(reference &&
        matchesAny(rawFieldValue(result, 'verselist'), scripturePatterns(reference)));
      return { result, index, matchCount, ratio: matchCount / versePairs.size, sameChapter, citesVerse };
    })
    .filter(item => item.matchCount >= 2 && item.ratio >= 0.15 && !isScriptureResult(item.result))
    .sort((left, right) => Number(right.citesVerse) - Number(left.citesVerse)
      || Number(right.sameChapter) - Number(left.sameChapter)
      || right.ratio - left.ratio
      || right.matchCount - left.matchCount
      || left.index - right.index)
    .slice(0, limit)
    .map(item => item.result);
};

export const isExactScriptureVerseResult = (result, reference) => Boolean(
  result
  && reference
  && reference.verseStart !== null
  && isScriptureResult(result)
  && [reference.book, reference.bookname].includes(
    plainText(rawFieldValue(result, 'chapter_code_name') || rawFieldValue(result, 'chapter_name')).trim()
  )
  && Number(rawFieldValue(result, 'chapter_number')) === reference.chapter
  && Number(rawFieldValue(result, 'verse_number')) === reference.verseStart
);

export const buildRelevanceSearchOptions = searchTerm => {
  const reference = parseReadingPlanReference(searchTerm);
  const isSingleVerse = reference
    && reference.verseStart !== null
    && reference.verseStart === reference.verseEnd
    && reference.chapter === reference.chapterEnd;

  return {
    search_fields: DEFAULT_SEARCH_FIELDS,
    // Do not apply scripture-specific precision to topical searches such as faith.
    ...(reference ? { precision: 8 } : {}),
    // Boost only the requested verse, rather than every Bible result. This
    // puts it into Elastic's first page without changing pagination or counts.
    ...(isSingleVerse ? {
      boosts: {
        chapter_verse_number: [{
          type: 'value',
          value: `${reference.chapter}:${reference.verseStart}`,
          operation: 'multiply',
          factor: 10,
        }],
      },
    } : {}),
  };
};

export const buildElasticSearchTerm = searchTerm => {
  const reference = parseReadingPlanReference(searchTerm);
  if (reference
      && reference.verseStart !== null
      && reference.verseStart === reference.verseEnd
      && reference.chapter === reference.chapterEnd) {
    // The index uses traditional Chinese book abbreviations. Normalize
    // simplified/full-name references so the exact verse is searchable too.
    return `${reference.book}${reference.chapter}:${reference.verseStart}`;
  }
  const isVerseRange = reference
    && reference.verseStart !== null
    && reference.verseEnd > reference.verseStart;
  if (!isVerseRange) return searchTerm;

  // Verse ranges are stored as individual Bible documents. Search the chapter
  // so Elastic can retrieve every verse, then rank the requested range first.
  return `${reference.book}${reference.chapter}`;
};

export const rerankScriptureSearchResponse = (response, searchTerm) => {
  const reference = parseReadingPlanReference(searchTerm);
  if (!Array.isArray(response?.results)) return response;

  const rankedResults = response.results
    .map((result, index) => ({
      result,
      index,
      typePriority: isScriptureResult(result) ? 1 : 0,
      referenceScore: reference ? scriptureRelevanceScore(result, reference) : 0,
    }))
    .sort((left, right) => (
      right.typePriority - left.typePriority
      || right.referenceScore - left.referenceScore
      || left.index - right.index
    ))
    .map(item => item.result);

  return { ...response, results: rankedResults };
};
